// meetingInfo.js -- builds the "copy meeting info" text for a lead or
// customer record. Used by the Copy Meeting Info button on LeadDetail and
// CustomerDetail; pastes into a Teams/Zoom invite.
//
// Output (Dave 2026-10-26):
//   Irving Oil Sable River, NS (Site #2611) - XSite Back Office Demo
//
//   Site:    Irving Oil Sable River, Site #2611, Sable River, NS (Bulloch POS)
//   Time:    Atlantic Time, 1 hour ahead of Toronto
//   Contact: Andy Blackburn, 902-656-2041, andy@example.com
//   Host:    XSite Group - David Cook, Managing Director, 905.346.0864 ext 102, david@xsitegroup.ca
//
// Leads get "XSite Back Office Demo" plus a Notes line telling the
// contact how to join; customers get "XSite Group Meeting". The first
// line works as the Teams meeting title. Lines with nothing to say are
// dropped.
const DEMO_NOTE = 'Join with the Teams link in this invite. Nothing to install; any computer with internet works.';

// Per-user dial-in strings. Keyed by email so it's robust to name
// changes / rebrands. Default falls back to the main office line.
const HOST_PHONES = {
  'david@xsitegroup.ca': '905.346.0864 ext 102',
  'mary@xsitegroup.ca':  '905.346.0864 ext 101',
  'zoe@xsitegroup.ca':   '905.346.0864',
  'kaden@xsitegroup.ca': '905.346.0864',
};
const DEFAULT_HOST_PHONE = '905.346.0864';

function hostPhoneFor(email) {
  if (!email) return DEFAULT_HOST_PHONE;
  return HOST_PHONES[String(email).toLowerCase()] || DEFAULT_HOST_PHONE;
}

// Time zone by province/state, so the invite says how far ahead/behind
// the contact is (NS = 1 hour ahead of Toronto). Computed with the real
// offsets on the day it's copied, so daylight time is handled.
const PROV_ZONE = {
  NL: 'America/St_Johns', NS: 'America/Halifax', PE: 'America/Halifax', NB: 'America/Moncton',
  QC: 'America/Toronto', ON: 'America/Toronto', MB: 'America/Winnipeg', SK: 'America/Regina',
  AB: 'America/Edmonton', BC: 'America/Vancouver', YT: 'America/Whitehorse', NT: 'America/Yellowknife',
  NU: 'America/Iqaluit',
};
const STATE_ZONE = {
  // Eastern
  CT: 'America/New_York', DE: 'America/New_York', DC: 'America/New_York', FL: 'America/New_York', GA: 'America/New_York',
  IN: 'America/Indiana/Indianapolis', KY: 'America/New_York', ME: 'America/New_York', MD: 'America/New_York',
  MA: 'America/New_York', MI: 'America/Detroit', NH: 'America/New_York', NJ: 'America/New_York', NY: 'America/New_York',
  NC: 'America/New_York', OH: 'America/New_York', PA: 'America/New_York', RI: 'America/New_York', SC: 'America/New_York',
  VT: 'America/New_York', VA: 'America/New_York', WV: 'America/New_York',
  // Central
  AL: 'America/Chicago', AR: 'America/Chicago', IL: 'America/Chicago', IA: 'America/Chicago', KS: 'America/Chicago',
  LA: 'America/Chicago', MN: 'America/Chicago', MS: 'America/Chicago', MO: 'America/Chicago', NE: 'America/Chicago',
  ND: 'America/Chicago', OK: 'America/Chicago', SD: 'America/Chicago', TN: 'America/Chicago', TX: 'America/Chicago',
  WI: 'America/Chicago',
  // Mountain / Pacific / other
  AZ: 'America/Phoenix', CO: 'America/Denver', ID: 'America/Boise', MT: 'America/Denver', NM: 'America/Denver',
  UT: 'America/Denver', WY: 'America/Denver', CA: 'America/Los_Angeles', NV: 'America/Los_Angeles',
  OR: 'America/Los_Angeles', WA: 'America/Los_Angeles', AK: 'America/Anchorage', HI: 'Pacific/Honolulu',
};
const PROV_NAMES = {
  'NEWFOUNDLAND AND LABRADOR': 'NL', NEWFOUNDLAND: 'NL', 'NOVA SCOTIA': 'NS', 'PRINCE EDWARD ISLAND': 'PE',
  'NEW BRUNSWICK': 'NB', QUEBEC: 'QC', 'QUÉBEC': 'QC', ONTARIO: 'ON', MANITOBA: 'MB', SASKATCHEWAN: 'SK',
  ALBERTA: 'AB', 'BRITISH COLUMBIA': 'BC', YUKON: 'YT', 'NORTHWEST TERRITORIES': 'NT', NUNAVUT: 'NU',
};
function provCode(v) {
  const t = String(v || '').trim().toUpperCase();
  return PROV_NAMES[t] || t;
}
function zoneOffsetMinutes(zone, when) {
  try {
    const part = new Intl.DateTimeFormat('en-US', { timeZone: zone, timeZoneName: 'longOffset' })
      .formatToParts(when).find((p) => p.type === 'timeZoneName')?.value || '';
    const m = part.match(/GMT([+-])(\d{1,2})(?::?(\d{2}))?/);
    if (!m) return part === 'GMT' ? 0 : null;
    return (m[1] === '-' ? -1 : 1) * (Number(m[2]) * 60 + Number(m[3] || 0));
  } catch (e) { return null; }
}
function zoneName(zone, when) {
  try {
    const n = new Intl.DateTimeFormat('en-CA', { timeZone: zone, timeZoneName: 'long' })
      .formatToParts(when).find((p) => p.type === 'timeZoneName')?.value || '';
    return n.replace(/ (Standard|Daylight)( Saving)? Time$/, ' Time');
  } catch (e) { return ''; }
}
// "Atlantic Time, 1 hour ahead of Toronto" / "Pacific Time, 3 hours behind Toronto"
export function timeZoneNote(province) {
  const code = provCode(province);
  const zone = PROV_ZONE[code] || STATE_ZONE[code];
  if (!zone) return '';
  const now = new Date();
  const theirs = zoneOffsetMinutes(zone, now);
  const ours = zoneOffsetMinutes('America/Toronto', now);
  if (theirs == null || ours == null) return '';
  const diff = theirs - ours;
  const name = zoneName(zone, now);
  if (diff === 0) return `${name}, same time as Toronto`;
  const h = Math.abs(diff) / 60;
  const amount = Number.isInteger(h) ? `${h} hour${h === 1 ? '' : 's'}` : `${h} hours`;
  return `${name}, ${amount} ${diff > 0 ? 'ahead of' : 'behind'} Toronto`;
}

function contactName(rec) {
  return [rec.first_name, rec.last_name].map((v) => (v || '').trim()).filter(Boolean).join(' ');
}

function locationString(rec) {
  return [rec.city, rec.province_state].filter(Boolean).join(', ');
}

// Prefer cell over main when both exist; reps will call the cell first.
function contactPhone(rec) {
  return rec.phone_cell || rec.phone_main || '';
}

// Full site name with the brand in front: "Irving Oil Sable River".
// Uses the company name when there is one (adding the fuel brand if the
// name doesn't already carry it), otherwise brand + city.
function siteName(rec) {
  const brand = (rec.fuel_brand && !/^(other|independent)$/i.test(rec.fuel_brand)) ? rec.fuel_brand : '';
  const company = (rec.company || '').trim();
  const norm = (s) => s.toLowerCase().replace(/[^a-z0-9]/g, '');
  if (company) {
    if (!brand || norm(company).includes(norm(brand))) return company;
    return `${brand} ${company}`;
  }
  return [brand, rec.city].filter(Boolean).join(' ');
}

export function buildMeetingInfo(record, user, { kind } = {}) {
  if (!record) return '';
  const isLead = kind ? kind === 'lead' : record.record_type === 'lead';
  const name = siteName(record);
  const siteNum = record.site_number ? `Site #${record.site_number}` : '';
  const loc = locationString(record);
  const pos = record.pos_type ? `${record.pos_type} POS` : '';

  // Leads: name the product being demoed. Customers: a general meeting.
  const what = isLead ? 'XSite Back Office Demo' : 'XSite Group Meeting';
  const prov = record.province_state ? provCode(record.province_state) : '';
  const titleSite = [[name, prov].filter(Boolean).join(', '), siteNum && `(${siteNum})`].filter(Boolean).join(' ');
  // Site first so it's readable in a narrow calendar slot.
  const title = titleSite ? `${titleSite} - ${what}` : what;


  const siteBits = [name, siteNum, loc].filter(Boolean).join(', ');
  const siteLine = siteBits ? `Site:    ${siteBits}${pos ? ` (${pos})` : ''}` : null;
  const tz = timeZoneNote(record.province_state);
  const tzLine = tz ? `Time:    ${tz}` : null;

  const cName = contactName(record);
  const contactBits = [cName, contactPhone(record), record.email1].filter(Boolean).join(', ');
  const contactLine = contactBits ? `Contact: ${contactBits}` : null;

  // Host = currently logged-in user. Title falls back to role.
  const hostName  = user?.name || [user?.first_name, user?.last_name].filter(Boolean).join(' ').trim() || '';
  const hostTitle = user?.title || (user?.role === 'admin' ? 'Managing Director' : '');
  const hostLine  = hostName
    ? `Host:    XSite Group - ${[hostName, hostTitle, hostPhoneFor(user?.email), user?.email].filter(Boolean).join(', ')}`
    : null;

  const notesLine = isLead ? `Notes:   ${DEMO_NOTE}` : null;

  return [title, '', siteLine, tzLine, contactLine, hostLine, notesLine].filter((l) => l !== null).join('\n');
}

// The note logged on the record when the meeting info is copied.
export function meetingLogNote(record, { kind } = {}) {
  const isLead = kind ? kind === 'lead' : record?.record_type === 'lead';
  const title = buildMeetingInfo(record, null, { kind }).split('\n')[0];
  return {
    type: isLead ? 'Demo' : 'Meeting',
    text: `${isLead ? 'Demo' : 'Meeting'} set up (meeting info copied for the invite): ${title}`,
  };
}
